import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Copy, Check, Users, UserCheck, Clock, Sparkles, Crown, Trophy, Heart, Loader2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "@/context/LanguageContext";
import { referralsApi } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const StatTile = ({ icon: Icon, label, value, accent, testid }) => (
    <div className="rounded-2xl border border-border bg-surface-1 p-4" data-testid={testid}>
        <div className="flex items-center justify-between">
            <span className="mono-label text-t4">{label}</span>
            <Icon className="h-4 w-4" style={{ color: accent }} />
        </div>
        <div className="mt-2 font-sans-display text-2xl font-semibold text-t1">{value}</div>
    </div>
);

const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const map = {
        active: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
        pending: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
        rejected: "bg-surface-3 text-t3",
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium", map[status] || map.pending)}>
            {t(`ambassador.statuses.${status}`)}
        </span>
    );
};

export default function Ambassador() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState("");

    useEffect(() => {
        (async () => {
            try { setData(await referralsApi.me()); } finally { setLoading(false); }
        })();
    }, []);

    if (loading) {
        return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;
    }

    const code = data?.code || "";
    const link = `${window.location.origin}/register?ref=${code}`;

    const copy = (value, key) => {
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(key);
            toast.success(t("ambassador.copied"));
            setTimeout(() => setCopied(""), 1800);
        }).catch(() => {});
    };

    const stats = data?.stats || {};
    const commission = data?.commission || {};
    const rewards = data?.rewards || {};
    const activeCount = rewards.active_count || 0;
    const milestones = rewards.milestones || [];
    const steps = t("ambassador.steps");
    const referrals = data?.referrals || [];
    const payouts = data?.payouts || [];

    const rewardLabel = (m) => (m.reward_tier === "premium" ? t("ambassador.premiumMonth") : t("ambassador.proMonth"));

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10" data-testid="ambassador-page">
            {/* Header */}
            <div className="max-w-2xl">
                <span className="mono-label text-t4">{t("ambassador.badge")}</span>
                <h1 className="mt-2 flex items-center gap-2.5 font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">
                    <Gift className="h-6 w-6 text-[hsl(var(--primary))]" /> {t("ambassador.title")}
                </h1>
                <p className="mt-2 text-t2">{t("ambassador.subtitle")}</p>
            </div>

            {/* Invite code + link */}
            <div className="mt-7 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-border bg-surface-1 p-5">
                    <div className="mono-label text-t4">{t("ambassador.yourCode")}</div>
                    <div className="mt-2 flex items-center gap-3">
                        <code data-testid="ambassador-code" className="flex-1 truncate rounded-xl border border-border bg-surface-2 px-4 py-3 font-mono-ui text-lg tracking-widest text-t1">{code}</code>
                        <Button data-testid="ambassador-copy-code" variant="secondary" className="gap-1.5 bg-surface-3/70" onClick={() => copy(code, "code")}>
                            {copied === "code" ? <Check className="h-4 w-4 text-[hsl(var(--success))]" /> : <Copy className="h-4 w-4" />}
                            {t("ambassador.copyCode")}
                        </Button>
                    </div>
                </div>
                <div className="rounded-2xl border border-border bg-surface-1 p-5">
                    <div className="mono-label text-t4">{t("ambassador.yourLink")}</div>
                    <div className="mt-2 flex items-center gap-3">
                        <code data-testid="ambassador-link" className="flex-1 truncate rounded-xl border border-border bg-surface-2 px-4 py-3 font-mono-ui text-sm text-t2">{link}</code>
                        <Button data-testid="ambassador-copy-link" className="gap-1.5" onClick={() => copy(link, "link")}>
                            {copied === "link" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            {t("ambassador.copyLink")}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatTile testid="ambassador-stat-total" icon={Users} label={t("ambassador.stats.total")} value={stats.total || 0} accent="hsl(206,70%,52%)" />
                <StatTile testid="ambassador-stat-active" icon={UserCheck} label={t("ambassador.stats.active")} value={stats.active || 0} accent="hsl(152,52%,46%)" />
                <StatTile testid="ambassador-stat-pending" icon={Clock} label={t("ambassador.stats.pending")} value={stats.pending || 0} accent="hsl(38,92%,58%)" />
            </div>

            {/* Rewards + Milestones */}
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
                {/* Rewards summary */}
                <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="ambassador-rewards">
                    <div className="flex items-center gap-2">
                        <Wallet className="h-4 w-4 text-[hsl(var(--primary))]" />
                        <h2 className="font-sans-display text-sm font-semibold text-t1">{t("ambassador.rewardsTitle")}</h2>
                    </div>
                    <p className="mt-1 text-xs text-t4">{t("ambassador.perReferral", { amount: commission.monthly_per_referral_eur ?? 2 })}</p>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="rounded-xl bg-surface-2 p-3">
                            <div className="mono-label text-t4">{t("ambassador.estimate")}</div>
                            <div className="mt-1 font-sans-display text-xl font-semibold text-t1" data-testid="ambassador-estimate">{commission.monthly_estimate_eur ?? 0} €</div>
                        </div>
                        <div className="rounded-xl bg-surface-2 p-3">
                            <div className="mono-label text-t4">{t("ambassador.approved")}</div>
                            <div className="mt-1 font-sans-display text-xl font-semibold text-t1">{commission.approved_monthly_eur ?? 0} €</div>
                        </div>
                        <div className="rounded-xl bg-surface-2 p-3">
                            <div className="mono-label text-t4">{t("ambassador.paidOut")}</div>
                            <div className="mt-1 font-sans-display text-xl font-semibold text-t1">{commission.paid_total_eur ?? 0} €</div>
                        </div>
                        <div className="rounded-xl bg-surface-2 p-3">
                            <div className="mono-label text-t4">{t("ambassador.pendingReview")}</div>
                            <div className="mt-1 font-sans-display text-xl font-semibold text-t1">{commission.pending_review_count ?? 0}</div>
                        </div>
                    </div>
                </div>

                {/* Milestones */}
                <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="ambassador-milestones">
                    <div className="flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-[hsl(var(--primary))]" />
                        <h2 className="font-sans-display text-sm font-semibold text-t1">{t("ambassador.milestonesTitle")}</h2>
                    </div>
                    <div className="mt-4 space-y-4">
                        {milestones.map((m) => {
                            const reached = activeCount >= m.count;
                            const pct = Math.min(100, Math.round((activeCount / m.count) * 100));
                            const Icon = m.reward_tier === "premium" ? Crown : Sparkles;
                            return (
                                <div key={m.count}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-sm text-t2">
                                            <Icon className={cn("h-4 w-4", reached ? "text-[hsl(var(--success))]" : "text-t4")} />
                                            {rewardLabel(m)}
                                        </div>
                                        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                                            reached ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-surface-3 text-t4")}>
                                            {reached ? t("ambassador.earned") : t("ambassador.locked")}
                                        </span>
                                    </div>
                                    <Progress value={pct} className="mt-2 h-1.5 bg-surface-3" />
                                    <div className="mt-1 mono-label text-[10px] text-t4">{t("ambassador.milestoneProgress", { count: activeCount, target: m.count })}</div>
                                </div>
                            );
                        })}
                        {rewards.next ? (
                            <p className="text-xs text-t3">{t("ambassador.nextReward", { remaining: rewards.next.count - activeCount, reward: rewardLabel(rewards.next) })}</p>
                        ) : (
                            <p className="text-xs text-[hsl(var(--success))]">{t("ambassador.allUnlocked")}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* How it works */}
            <div className="mt-8">
                <div className="mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" /><h2 className="font-sans-display text-sm font-semibold text-t2">{t("ambassador.howItWorks")}</h2></div>
                <div className="grid gap-4 sm:grid-cols-3">
                    {(Array.isArray(steps) ? steps : []).map((s, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="rounded-2xl border border-border bg-surface-2 p-5 shadow-elev-1">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/15 font-sans-display text-sm font-semibold text-[hsl(var(--primary))]">{i + 1}</div>
                            <div className="mt-3 font-sans-display text-sm font-semibold text-t1">{s.title}</div>
                            <p className="mt-1 text-sm text-t3">{s.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Members table */}
            <div className="mt-8">
                <div className="mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-[hsl(var(--primary))]" /><h2 className="font-sans-display text-sm font-semibold text-t2">{t("ambassador.membersTitle")}</h2></div>
                {referrals.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-10 text-center text-sm text-t3" data-testid="ambassador-members-empty">{t("ambassador.membersEmpty")}</div>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-border bg-surface-1">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left">
                                    <th className="px-4 py-3 mono-label text-t4">{t("ambassador.member")}</th>
                                    <th className="px-4 py-3 mono-label text-t4">{t("ambassador.status")}</th>
                                    <th className="px-4 py-3 mono-label text-t4">{t("ambassador.joined")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {referrals.map((r, i) => (
                                    <tr key={i} className="border-b border-border/60 last:border-0" data-testid="ambassador-member-row">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-t1">{r.name}</div>
                                            <div className="text-xs text-t4">{r.email_masked}</div>
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                                        <td className="px-4 py-3 text-t3">{(r.created_at || "").slice(0, 10)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Payouts (only if present) */}
            {payouts.length > 0 && (
                <div className="mt-8">
                    <div className="mb-3 flex items-center gap-2"><Wallet className="h-4 w-4 text-[hsl(var(--primary))]" /><h2 className="font-sans-display text-sm font-semibold text-t2">{t("ambassador.payoutsTitle")}</h2></div>
                    <div className="overflow-hidden rounded-2xl border border-border bg-surface-1">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left">
                                    <th className="px-4 py-3 mono-label text-t4">{t("ambassador.amount")}</th>
                                    <th className="px-4 py-3 mono-label text-t4">{t("ambassador.period")}</th>
                                    <th className="px-4 py-3 mono-label text-t4">{t("ambassador.status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payouts.map((p, i) => (
                                    <tr key={i} className="border-b border-border/60 last:border-0">
                                        <td className="px-4 py-3 font-medium text-t1">{p.amount_eur} €</td>
                                        <td className="px-4 py-3 text-t3">{p.period || "—"}</td>
                                        <td className="px-4 py-3 text-t3">{p.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Community note */}
            <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-5" data-testid="ambassador-note">
                <Heart className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
                <p className="text-sm text-t2">{t("ambassador.note")}</p>
            </div>
        </div>
    );
}
